// client/src/pages/marketing/PrivacyPage.jsx
import React from 'react';
import Navbar from '../../components/marketing/Navbar';
import Hero from '../../components/marketing/Hero';
import Footer from '../../components/marketing/Footer';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-cream-100 text-charcoal-800 font-['Lexend_Deca'] text-base leading-relaxed">
      <Navbar />

      <Hero
        title="Privacyverklaring"
        subtitle="Versie 1.0 | Datum inwerkingtreding: 1 maart 2026"
        height="normal"
      />

      <main className="mx-auto max-w-3xl py-20 px-5">

        {/* Section 1 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">1. Inleiding</h2>
          <p className="mb-4">
            Leren Motiveren hecht grote waarde aan uw privacy en de bescherming van uw persoonsgegevens.
            In deze privacyverklaring leggen wij uit welke persoonsgegevens wij verzamelen, waarom wij
            deze verzamelen, hoe lang wij deze bewaren en welke rechten u heeft.
          </p>
          <p className="mb-4">
            Deze privacyverklaring is van toepassing op alle diensten die Leren Motiveren aanbiedt via
            het platform www.lerenmotiveren.nl.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Verantwoordelijke voor de gegevensverwerking:</h3>
          <address className="not-italic mb-4">
            Leren Motiveren<br />
            Oudwijkerdwarsstraat 73<br />
            3581LB Utrecht<br />
            KVK-nummer: 99583755<br />
            E-mail: <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>
          </address>
          <p>
            Voor vragen over privacy of de verwerking van uw persoonsgegevens kunt u contact met ons
            opnemen via bovenstaand e-mailadres.
          </p>
        </section>

        {/* Section 2 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">2. Welke persoonsgegevens verzamelen wij?</h2>
          <p className="mb-4">Wij verzamelen en verwerken de volgende categorieën persoonsgegevens:</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.1 Accountgegevens</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Voor- en achternaam</li>
            <li>E-mailadres</li>
            <li>Wachtwoord (versleuteld opgeslagen)</li>
            <li>Beroepsregistratienummer (indien van toepassing voor registratie van Geaccrediteerde Punten)</li>
            <li>Accountaanmaakdatum</li>
            <li>Laatste inlogdatum</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.2 Cursusgegevens en voortgang</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Voortgang per module en oefening</li>
            <li>Voltooiingsstatus van cursussen</li>
            <li>Behaalde scores en resultaten</li>
            <li>Tijdsbesteding per module</li>
            <li>Certificaten en behaalde accreditatiepunten</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.3 Conversatiegegevens (AI-interacties)</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Alle conversaties die u voert met onze AI-gestuurde persona's</li>
            <li>Uw vragen en antwoorden tijdens oefeningen</li>
            <li>AI-gegenereerde responses</li>
            <li>Context en metadata van conversaties</li>
            <li>Tijdstempels van interacties</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.4 Evaluatie- en feedbackgegevens</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>AI-evaluaties van uw prestaties</li>
            <li>Feedback op oefeningen en simulaties</li>
            <li>Analyse van uw gespreksvaardigheden</li>
            <li>Leerresultaten en verbeterpunten</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.5 Technische gegevens</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>IP-adres</li>
            <li>Browsertype en -versie</li>
            <li>Apparaattype (desktop, tablet, mobiel)</li>
            <li>Besturingssysteem</li>
            <li>Sessiegegevens</li>
            <li>Foutmeldingen en technische logs</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.6 Betalingsgegevens</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Transactiegegevens (via Mollie)</li>
            <li>Betaalstatus en facturatiegegevens</li>
            <li><strong>Let op:</strong> kredietkaartgegevens worden niet door ons opgeslagen maar direct verwerkt door onze betalingsprovider Mollie</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">2.7 Communicatiegegevens</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>E-mailcorrespondentie met onze support</li>
            <li>Feedback en suggesties die u met ons deelt</li>
            <li>Voorkeuren voor nieuwsbrieven en marketing (indien u hiervoor toestemming heeft gegeven)</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">3. Waarom verzamelen wij deze gegevens?</h2>
          <p className="mb-4">Wij verwerken uw persoonsgegevens voor de volgende doeleinden en op basis van de volgende rechtsgronden:</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.1 Uitvoering van de overeenkomst (Art. 6 lid 1 sub b AVG)</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Account beheer:</strong> Aanmaken, beheren en beveiligen van uw account</li>
            <li><strong>Toegang verlenen:</strong> Verlenen van toegang tot aangekochte cursussen</li>
            <li><strong>Cursuslevering:</strong> Leveren van cursusmateriaal, oefeningen en AI-simulaties</li>
            <li><strong>Voortgang bijhouden:</strong> Opslaan van uw leervoortgang zodat u kunt verder gaan waar u gebleven bent</li>
            <li><strong>Certificering:</strong> Genereren en verstrekken van certificaten bij voltooiing</li>
            <li><strong>Registratie van Geaccrediteerde Punten:</strong> Aanmelden van behaalde punten bij de betreffende accrediterende instantie, indien van toepassing</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.2 Gerechtvaardigd belang (Art. 6 lid 1 sub f AVG)</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Platformverbetering:</strong> Analyseren van (geanonimiseerde) cursusgegevens om de kwaliteit van onze cursussen en AI-technologie te verbeteren</li>
            <li><strong>Technische support:</strong> Oplossen van technische problemen en verbeteren van de gebruikerservaring</li>
            <li><strong>Fraudepreventie:</strong> Detecteren en voorkomen van misbruik van het platform</li>
            <li><strong>Beveiliging:</strong> Waarborgen van de veiligheid en integriteit van ons platform</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.3 Toestemming (Art. 6 lid 1 sub a AVG)</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Marketing:</strong> Versturen van nieuwsbrieven en promotionele e-mails (alleen met uw voorafgaande toestemming)</li>
            <li><strong>Niet-essentiële cookies:</strong> Plaatsen van niet-noodzakelijke cookies (indien van toepassing)</li>
          </ul>
          <p className="mt-2">U kunt uw toestemming te allen tijde intrekken zonder dat dit afbreuk doet aan de rechtmatigheid van de verwerking vóór de intrekking.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">3.4 Wettelijke verplichting (Art. 6 lid 1 sub c AVG)</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Administratie:</strong> Bewaren van financiële gegevens conform fiscale wetgeving (7 jaar)</li>
            <li><strong>Registratie van Geaccrediteerde Punten:</strong> Voldoen aan registratieverplichtingen van de betreffende accrediterende instantie, indien van toepassing</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">4. Gebruik van AI-technologie en geautomatiseerde besluitvorming</h2>

          <h3 className="text-lg font-semibold mt-6 mb-2">4.1 AI-gestuurde leerervaringen</h3>
          <p className="mb-4">Ons platform maakt gebruik van geavanceerde kunstmatige intelligentie (AI), specifiek de Claude API van Anthropic, om:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Realistische conversatiesimulaties te genereren</li>
            <li>Gepersonaliseerde feedback te geven op uw gespreksvaardigheden</li>
            <li>Uw prestaties te evalueren</li>
            <li>Adaptieve leertrajecten aan te bieden</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">4.2 Geautomatiseerde evaluaties</h3>
          <p className="mb-2">De AI-technologie analyseert uw conversaties en oefeningen om automatisch:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Uw gebruik van motiverende gespreksvoering te beoordelen</li>
            <li>Verbeterpunten te identificeren</li>
            <li>Scores toe te kennen aan oefeningen</li>
            <li>Aanbevelingen te doen voor vervolgstappen</li>
          </ul>

          <div className="mt-4 p-4 bg-purple-50 rounded-lg">
            <h4 className="font-semibold mb-2">Belangrijke toelichting:</h4>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Deze evaluaties dienen uitsluitend <strong>educatieve doeleinden</strong> en hebben geen juridische of anderszins significante gevolgen voor u</li>
              <li>De AI-evaluaties zijn onderdeel van het leerproces en niet bedoeld als professionele beoordeling of accreditatie</li>
              <li>U behoudt altijd controle over uw leerproces en kunt oefeningen herhalen</li>
              <li>Voor officiële certificering geldt voltooiing van de volledige cursus als criterium</li>
            </ul>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-2">4.3 Transparantie en menselijke tussenkomst</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>De AI-gegenereerde responses kunnen incidenteel onvolkomenheden bevatten</li>
            <li>Bij twijfel over AI-feedback kunt u altijd contact opnemen met onze support voor menselijke beoordeling</li>
            <li>Conversatiegegevens worden gemonitord om de kwaliteit te waarborgen</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">4.4 Gevoelige informatie in simulaties</h3>
          <p className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <strong>Let op:</strong> Deel geen echte patiëntgegevens, vertrouwelijke medische informatie of andere gevoelige persoonsgegevens van derden tijdens de simulaties. De oefeningen zijn bedoeld voor fictieve scenario's en het oefenen van gespreksvaardigheden.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">5. Hoe lang bewaren wij uw gegevens?</h2>
          <p className="mb-4">Wij bewaren uw persoonsgegevens niet langer dan noodzakelijk voor de doeleinden waarvoor ze zijn verzameld:</p>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-gray-300 mt-4">
              <thead>
                <tr className="bg-purple-100">
                  <th className="border border-gray-300 px-4 py-2 text-left">Categorie gegevens</th>
                  <th className="border border-gray-300 px-4 py-2 text-left">Bewaartermijn</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Accountgegevens</td>
                  <td className="border border-gray-300 px-4 py-2">Tijdens actief account + 12 maanden</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">Cursusvoortgang en resultaten</td>
                  <td className="border border-gray-300 px-4 py-2">Tijdens actief account + 12 maanden</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Conversatiegegevens (AI-interacties)</td>
                  <td className="border border-gray-300 px-4 py-2">Tijdens actief account + 12 maanden</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">Evaluatie- en feedbackgegevens</td>
                  <td className="border border-gray-300 px-4 py-2">Tijdens actief account + 12 maanden</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Betalingsgegevens (facturen)</td>
                  <td className="border border-gray-300 px-4 py-2">7 jaar (wettelijke bewaarplicht)</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">Registratiegegevens Geaccrediteerde Punten</td>
                  <td className="border border-gray-300 px-4 py-2">Conform voorschriften van de betreffende accrediterende instantie</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Supportcorrespondentie</td>
                  <td className="border border-gray-300 px-4 py-2">2 jaar na laatste contact</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">Technische logs</td>
                  <td className="border border-gray-300 px-4 py-2">90 dagen</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">Marketing communicatie</td>
                  <td className="border border-gray-300 px-4 py-2">Tot u zich afmeldt of bezwaar maakt</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold mb-2">Toelichting:</h4>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><strong>Tijdens actief account:</strong> zolang uw account actief is en u toegang heeft tot cursussen</li>
              <li><strong>Na account verwijdering:</strong> binnen 30 dagen worden alle persoonsgegevens verwijderd, behoudens gegevens die wij wettelijk moeten bewaren</li>
              <li><strong>Geanonimiseerde gegevens:</strong> kunnen langer worden bewaard voor statistische analyse en platformverbetering</li>
            </ul>
          </div>
        </section>

        {/* Section 6 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">6. Met wie delen wij uw gegevens?</h2>
          <p className="mb-4"><strong>Wij verkopen uw persoonsgegevens nooit aan derden voor commerciële doeleinden.</strong></p>
          <p className="mb-4">Wij delen uw gegevens alleen met de volgende partijen die ons helpen bij het leveren van onze diensten:</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">6.1 Verwerkersovereenkomsten (Art. 28 AVG)</h3>

          <div className="space-y-4 mt-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold">Anthropic (Claude API)</h4>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Doel:</strong> Leveren van AI-technologie voor conversatiesimulaties en evaluaties</li>
                <li><strong>Gegevens:</strong> Conversatiegegevens, context voor AI-responses</li>
                <li><strong>Locatie:</strong> Verenigde Staten / EU (afhankelijk van datacenter)</li>
                <li><strong>Waarborg:</strong> Standard Contractual Clauses (SCC) conform AVG</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold">Mollie</h4>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Doel:</strong> Betalingsverwerking</li>
                <li><strong>Gegevens:</strong> Transactiegegevens, betaalstatus</li>
                <li><strong>Locatie:</strong> Nederland (EU)</li>
                <li><strong>Waarborg:</strong> AVG-compliant EU-provider</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold">Proton Mail</h4>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Doel:</strong> Versturen van transactionele e-mails (toegangsgegevens, certificaten, support)</li>
                <li><strong>Gegevens:</strong> E-mailadres, e-mailcontent</li>
                <li><strong>Locatie:</strong> Zwitserland (adequaatheidsbesluit EU)</li>
                <li><strong>Waarborg:</strong> AVG-compliant, end-to-end versleuteling, Zwitserse privacywetgeving</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold">Render (Hosting)</h4>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Doel:</strong> Hosting van platform en database</li>
                <li><strong>Gegevens:</strong> Alle platformgegevens</li>
                <li><strong>Locatie:</strong> EU-datacenters</li>
                <li><strong>Waarborg:</strong> AVG-compliant hosting provider</li>
              </ul>
            </div>

            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-semibold">MongoDB</h4>
              <ul className="list-disc list-inside ml-4 mt-2 space-y-1">
                <li><strong>Doel:</strong> Database opslag</li>
                <li><strong>Gegevens:</strong> Alle opgeslagen platformgegevens</li>
                <li><strong>Locatie:</strong> EU (via Render hosting)</li>
                <li><strong>Waarborg:</strong> Versleutelde opslag, toegangscontroles</li>
              </ul>
            </div>
          </div>

          <h3 className="text-lg font-semibold mt-6 mb-2">6.2 Wettelijke verplichting</h3>
          <p className="mb-2">Wij kunnen uw gegevens delen indien wij hiertoe wettelijk verplicht zijn, bijvoorbeeld:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Op basis van een gerechtelijk bevel</li>
            <li>Om te voldoen aan toepasselijke wet- en regelgeving</li>
            <li>Om onze rechten, eigendom of veiligheid te beschermen</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">6.3 Accrediterende instanties</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Doel:</strong> Registreren van behaalde Geaccrediteerde Punten, indien van toepassing op de gevolgde Cursus</li>
            <li><strong>Gegevens:</strong> Naam, beroepsregistratienummer, cursusgegevens, voltooiingsdatum</li>
            <li><strong>Rechtsgrond:</strong> Uitvoering overeenkomst</li>
          </ul>
        </section>

        {/* Section 7 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">7. Internationale doorgifte van gegevens</h2>
          <p className="mb-4">
            Sommige van onze dienstverleners (met name Anthropic voor de Claude API) kunnen servers buiten de Europese
            Economische Ruimte (EER) gebruiken, waaronder in de Verenigde Staten.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Waarborgen:</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Wij zorgen ervoor dat internationale doorgiften plaatsvinden conform de AVG</li>
            <li>Gebruik van <strong>Standard Contractual Clauses (SCC's)</strong> zoals goedgekeurd door de Europese Commissie</li>
            <li>Dienstverleners voldoen aan het <strong>EU-US Data Privacy Framework</strong> (voor VS-overdrachten)</li>
            <li>Technische en organisatorische beveiligingsmaatregelen zijn op orde</li>
          </ul>
          <p className="mt-4">
            U heeft het recht om meer informatie te vragen over de waarborgen die wij treffen bij internationale doorgifte.
          </p>
        </section>

        {/* Section 8 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">8. Beveiliging van uw gegevens</h2>
          <p className="mb-4">
            Wij nemen de beveiliging van uw persoonsgegevens zeer serieus en hebben passende technische en
            organisatorische maatregelen getroffen, waaronder:
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">8.1 Technische maatregelen</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Versleuteling:</strong> Alle communicatie verloopt via versleutelde verbindingen (HTTPS/TLS)</li>
            <li><strong>Wachtwoordbeveiliging:</strong> Wachtwoorden worden versleuteld opgeslagen (hashing met bcrypt)</li>
            <li><strong>Toegangscontrole:</strong> Strikte toegangsbeheer tot databases en systemen</li>
            <li><strong>Firewalls:</strong> Bescherming tegen ongeautoriseerde toegang</li>
            <li><strong>Regular updates:</strong> Systemen worden up-to-date gehouden met beveiligingspatches</li>
            <li><strong>Backups:</strong> Regelmatige back-ups met versleutelde opslag</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">8.2 Organisatorische maatregelen</h3>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Privacy by Design:</strong> Privacy is vanaf het ontwerp ingebouwd in onze systemen</li>
            <li><strong>Minimale toegang:</strong> Alleen geautoriseerd personeel heeft toegang tot persoonsgegevens</li>
            <li><strong>Monitoring:</strong> Continue monitoring van verdachte activiteiten</li>
            <li><strong>Incident response:</strong> Procedures voor het melden en afhandelen van datalekken</li>
            <li><strong>Awareness:</strong> Bewustwording en training over gegevensbescherming</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">8.3 Uw eigen verantwoordelijkheid</h3>
          <p className="mb-2">Ook u speelt een rol in de beveiliging:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Houd uw inloggegevens geheim</li>
            <li>Gebruik een sterk, uniek wachtwoord</li>
            <li>Log uit na gebruik op gedeelde computers</li>
            <li>Meld verdachte activiteiten direct aan ons</li>
          </ul>
          <p className="mt-4">
            Ondanks deze maatregelen kan er nooit 100% zekerheid worden gegeven. Bij een datalek melden wij dit
            conform de AVG binnen 72 uur aan de Autoriteit Persoonsgegevens en informeren wij u indien er een
            hoog risico bestaat voor uw rechten en vrijheden.
          </p>
        </section>

        {/* Section 9 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">9. Uw rechten</h2>
          <p className="mb-4">Op grond van de AVG heeft u de volgende rechten met betrekking tot uw persoonsgegevens:</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.1 Recht op inzage (Art. 15 AVG)</h3>
          <p>U heeft het recht om te weten welke persoonsgegevens wij van u verwerken. U kunt een verzoek indienen voor een kopie van uw gegevens.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.2 Recht op rectificatie (Art. 16 AVG)</h3>
          <p>U heeft het recht om onjuiste of onvolledige persoonsgegevens te laten corrigeren. Dit kunt u deels zelf doen in uw accountinstellingen.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.3 Recht op verwijdering / 'recht op vergetelheid' (Art. 17 AVG)</h3>
          <p className="mb-2">U heeft het recht om uw persoonsgegevens te laten verwijderen, bijvoorbeeld wanneer:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>De gegevens niet langer nodig zijn voor de doeleinden</li>
            <li>U uw toestemming intrekt en er geen andere rechtsgrond is</li>
            <li>U bezwaar maakt en er geen dwingende gerechtvaardigde gronden zijn</li>
            <li>De gegevens onrechtmatig zijn verwerkt</li>
          </ul>
          <p className="mt-2"><strong>Let op:</strong> Dit recht is niet absoluut. Wij mogen gegevens bewaren indien er een wettelijke bewaarplicht geldt (zoals voor facturatiegegevens).</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.4 Recht op beperking van de verwerking (Art. 18 AVG)</h3>
          <p className="mb-2">U kunt verzoeken om de verwerking van uw gegevens te beperken in bepaalde situaties, bijvoorbeeld:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Als u de juistheid van de gegevens betwist</li>
            <li>Als de verwerking onrechtmatig is maar u geen verwijdering wenst</li>
            <li>Als wij de gegevens niet meer nodig hebben maar u deze nog nodig heeft voor een rechtsvordering</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.5 Recht op dataportabiliteit (Art. 20 AVG)</h3>
          <p className="mb-2">
            U heeft het recht om uw persoonsgegevens in een gestructureerd, gangbaar en machineleesbaar formaat te
            ontvangen en deze naar een andere dienstverlener over te dragen. Dit geldt voor gegevens die:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>U zelf aan ons heeft verstrekt</li>
            <li>Wij verwerken op basis van toestemming of uitvoering van een overeenkomst</li>
            <li>De verwerking geautomatiseerd plaatsvindt</li>
          </ul>
          <p className="mt-2"><strong>Bijvoorbeeld:</strong> U kunt uw cursusvoortgang en resultaten exporteren in JSON-formaat.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.6 Recht van bezwaar (Art. 21 AVG)</h3>
          <p className="mb-2">
            U heeft het recht om bezwaar te maken tegen verwerking van uw gegevens op grond van ons gerechtvaardigd
            belang. Dit geldt met name voor:
          </p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Directe marketing (u kunt zich altijd afmelden)</li>
            <li>Profilering voor andere doeleinden dan uitvoering van de overeenkomst</li>
          </ul>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.7 Geen geautomatiseerde besluitvorming met rechtsgevolgen (Art. 22 AVG)</h3>
          <p className="mb-2">U heeft het recht om niet te worden onderworpen aan volledig geautomatiseerde besluitvorming met rechtsgevolgen.</p>
          <p className="p-4 bg-purple-50 rounded-lg mt-2">
            <strong>Toelichting bij Leren Motiveren:</strong> Hoewel wij AI gebruiken voor evaluaties, hebben deze geen
            rechtsgevolgen of vergelijkbare significante gevolgen. Het zijn educatieve beoordelingen als onderdeel van
            uw leerproces. Bovendien kunt u altijd contact opnemen voor menselijke beoordeling.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">9.8 Hoe oefent u uw rechten uit?</h3>
          <p className="mb-2">
            U kunt uw rechten uitoefenen door een e-mail te sturen naar: <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>
          </p>
          <p className="mb-2">In uw verzoek vermeld u:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Uw volledige naam en e-mailadres</li>
            <li>Welk recht u wilt uitoefenen</li>
            <li>Een duidelijke omschrijving van uw verzoek</li>
          </ul>
          <p className="mt-4 mb-2"><strong>Onze reactie:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Wij reageren binnen 1 maand op uw verzoek</li>
            <li>Bij complexe verzoeken kunnen wij dit met 2 maanden verlengen (u wordt hiervan op de hoogte gesteld)</li>
            <li>Wij kunnen om identificatie vragen om misbruik te voorkomen</li>
            <li>In de meeste gevallen is het uitoefenen van uw rechten gratis</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">10. Cookies</h2>

          <h3 className="text-lg font-semibold mt-6 mb-2">10.1 Wat zijn cookies?</h3>
          <p>
            Cookies zijn kleine tekstbestanden die op uw apparaat worden geplaatst wanneer u onze website bezoekt.
            Cookies helpen ons om de website te laten functioneren en uw ervaring te verbeteren.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">10.2 Welke cookies gebruiken wij?</h3>
          <p className="mb-2"><strong>Noodzakelijke (functionele) cookies</strong> - Deze cookies zijn essentieel voor het functioneren van het platform:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Sessiecookies:</strong> Om u ingelogd te houden tijdens uw sessie</li>
            <li><strong>Beveiligingscookies:</strong> Om CSRF-aanvallen te voorkomen</li>
            <li><strong>Voorkeurscookies:</strong> Om uw taalvoorkeur te onthouden</li>
          </ul>
          <p className="mt-2">Deze cookies vereisen geen toestemming omdat ze strikt noodzakelijk zijn.</p>

          <p className="mt-4">
            <strong>Analytische cookies (indien van toepassing)</strong> - Als wij analytische cookies gebruiken
            (bijvoorbeeld Google Analytics) om het gebruik van het platform te analyseren, vragen wij hiervoor
            vooraf uw toestemming.
          </p>

          <p className="mt-4">
            <strong>Marketing cookies</strong> - Wij gebruiken momenteel geen marketing of tracking cookies van derden.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">10.3 Cookies beheren</h3>
          <p className="mb-2">U kunt cookies beheren via uw browserinstellingen:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li><strong>Blokkeren:</strong> Alle cookies blokkeren (kan functionaliteit beperken)</li>
            <li><strong>Verwijderen:</strong> Bestaande cookies verwijderen</li>
            <li><strong>Selectief:</strong> Alleen bepaalde cookies toestaan</li>
          </ul>
          <p className="mt-2">Voor instructies per browser, raadpleeg de helpfunctie van uw browser.</p>
        </section>

        {/* Section 11 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">11. Links naar andere websites</h2>
          <p className="mb-4">
            Ons platform kan links bevatten naar externe websites (bijvoorbeeld websites van accrediterende instanties, Anthropic documentatie).
            Deze privacyverklaring is alleen van toepassing op onze eigen website.
          </p>
          <p>
            Wij zijn niet verantwoordelijk voor het privacybeleid van externe websites. Wij raden u aan om het
            privacybeleid van die websites te lezen.
          </p>
        </section>

        {/* Section 12 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">12. Wijzigingen in deze privacyverklaring</h2>
          <p className="mb-2">Wij kunnen deze privacyverklaring van tijd tot tijd aanpassen, bijvoorbeeld bij:</p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Wijzigingen in wetgeving</li>
            <li>Nieuwe functionaliteiten op het platform</li>
            <li>Wijzigingen in dienstverleners</li>
          </ul>

          <p className="mt-4 mb-2"><strong>Bij materiële wijzigingen:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Informeren wij u via e-mail</li>
            <li>Plaatsen wij een melding op het platform</li>
            <li>Wijzigingen treden in werking 30 dagen na kennisgeving</li>
          </ul>

          <p className="mt-4">
            De meest actuele versie van deze privacyverklaring is altijd beschikbaar op deze pagina.
            Aan de bovenkant van deze verklaring vindt u de datum van de laatste wijziging.
          </p>
        </section>

        {/* Section 13 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">13. Klachten en vragen</h2>

          <h3 className="text-lg font-semibold mt-6 mb-2">13.1 Contact opnemen</h3>
          <p className="mb-4">
            Heeft u vragen over deze privacyverklaring of over hoe wij met uw persoonsgegevens omgaan?
            Neem gerust contact met ons op:
          </p>
          <address className="not-italic mb-4 p-4 bg-gray-50 rounded-lg">
            <strong>Leren Motiveren</strong><br />
            E-mail: <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a><br />
            Adres: Oudwijkerdwarsstraat 73, 3581LB Utrecht
          </address>
          <p>Wij streven ernaar om binnen 2 werkdagen te reageren op uw vragen.</p>

          <h3 className="text-lg font-semibold mt-6 mb-2">13.2 Klacht indienen bij toezichthouder</h3>
          <p className="mb-4">
            Bent u niet tevreden over hoe wij met uw persoonsgegevens omgaan? U heeft altijd het recht om een
            klacht in te dienen bij de toezichthouder:
          </p>
          <address className="not-italic mb-4 p-4 bg-gray-50 rounded-lg">
            <strong>Autoriteit Persoonsgegevens</strong><br />
            Postbus 93374<br />
            2509 AJ Den Haag<br />
            Telefoon: 088 - 1805 250<br />
            Website: <a href="https://www.autoriteitpersoonsgegevens.nl" target="_blank" rel="noopener noreferrer" className="text-purple-600 hover:text-purple-800">www.autoriteitpersoonsgegevens.nl</a>
          </address>
          <p>
            Wij horen natuurlijk graag eerst van u, zodat wij kunnen proberen uw klacht op te lossen voordat u
            naar de Autoriteit Persoonsgegevens stapt.
          </p>
        </section>

        {/* Section 14 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">14. Bijzondere categorieën persoonsgegevens</h2>
          <p className="mb-4">
            Wij verwerken in principe <strong>geen bijzondere persoonsgegevens</strong> (zoals medische gegevens,
            religieuze overtuiging, strafrechtelijke gegevens) zoals bedoeld in artikel 9 AVG.
          </p>

          <div className="p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded">
            <h4 className="font-semibold mb-2">Belangrijke waarschuwing:</h4>
            <p className="mb-2">
              Tijdens de AI-gestuurde conversatiesimulaties kunt u in theorie gevoelige informatie delen.
              Wij verzoeken u dringend om:
            </p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li><strong>Geen echte patiëntgegevens</strong> te gebruiken in simulaties</li>
              <li><strong>Geen identificeerbare medische informatie</strong> van uzelf of anderen te delen</li>
              <li>Fictieve scenario's en algemene voorbeelden te gebruiken</li>
            </ul>
            <p className="mt-2">
              Mocht u per ongeluk toch gevoelige informatie hebben gedeeld, neem dan contact met ons op via{' '}
              <a href="mailto:info@lerenmotiveren.nl" className="text-purple-600 hover:text-purple-800">info@lerenmotiveren.nl</a>{' '}
              zodat wij deze kunnen verwijderen.
            </p>
          </div>
        </section>

        {/* Section 15 */}
        <section className="mb-12">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">15. Minderjarigen</h2>
          <p className="mb-4">
            Ons platform is primair bedoeld voor professionals in de gezondheidszorg. Studenten kunnen ook
            toegang hebben tot de cursussen.
          </p>

          <h3 className="text-lg font-semibold mt-6 mb-2">Leeftijdsgrens:</h3>
          <p className="mb-4">
            Voor personen onder de 16 jaar is toestemming van een ouder of voogd vereist voor het verwerken van
            persoonsgegevens. Indien u jonger bent dan 16 jaar, vragen wij u om eerst toestemming aan uw ouders
            of voogd te vragen voordat u een account aanmaakt.
          </p>
          <p>
            Wij verwerken nooit bewust persoonsgegevens van personen jonger dan 16 jaar zonder ouderlijke
            toestemming. Mocht u ontdekken dat dit wel het geval is, neem dan contact met ons op zodat wij de
            gegevens kunnen verwijderen.
          </p>
        </section>

        {/* Footer info */}
        <section className="mt-16 pt-8 border-t border-gray-300">
          <p className="text-sm text-gray-600 italic">
            Laatste wijziging: 1 maart 2026<br />
            Versie: 1.0
          </p>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
